import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, Loader2, ImageIcon, Award } from "lucide-react";
import { api, resolveImageUri } from "@/lib/api";
import { toast } from "sonner";

function Gallery({ title, icon, items, onUpload, onDelete, uploading, testidPrefix, hint }) {
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-2xl text-ink flex items-center gap-2">{icon} {title}</h3>
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-brand text-white text-sm font-semibold cursor-pointer" data-testid={`${testidPrefix}-upload`}>
          <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>
      {hint && <p className="text-xs text-muted2 mb-3">{hint}</p>}
      {items.length === 0 ? (
        <p className="text-sm text-muted2">None yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {items.map((it) => (
            <div key={it.id} className="relative rounded-xl overflow-hidden border border-line group" data-testid={`${testidPrefix}-${it.id}`}>
              <img src={resolveImageUri(it.image)} alt="" className="w-full h-32 object-cover" />
              <button onClick={() => onDelete(it.id)} data-testid={`${testidPrefix}-delete-${it.id}`} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-paper/95 shadow text-err flex items-center justify-center"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function MediaAdmin() {
  const qc = useQueryClient();
  const { data: banners = [] } = useQuery({ queryKey: ["banners"], queryFn: () => api.listBanners() });
  const { data: certs = [] } = useQuery({ queryKey: ["certificates"], queryFn: () => api.listCertificates() });
  const [uploading, setUploading] = useState("");

  const doUpload = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(kind);
    try {
      const r = await api.uploadImage(file);
      if (kind === "banner") { await api.addBanner(r.image_url); qc.invalidateQueries({ queryKey: ["banners"] }); }
      else { await api.addCertificate(r.image_url); qc.invalidateQueries({ queryKey: ["certificates"] }); }
      toast.success("Uploaded");
    } catch (err) { toast.error(err.message); } finally { setUploading(""); e.target.value = ""; }
  };
  const delBanner = async (id) => { try { await api.deleteBanner(id); qc.invalidateQueries({ queryKey: ["banners"] }); } catch (err) { toast.error(err.message); } };
  const delCert = async (id) => { try { await api.deleteCertificate(id); qc.invalidateQueries({ queryKey: ["certificates"] }); } catch (err) { toast.error(err.message); } };

  return (
    <div>
      <h2 className="font-display text-2xl text-ink mb-5">Media</h2>
      <Gallery title="Home Banners" icon={<ImageIcon className="text-brand" size={20} />} items={banners} onUpload={(e) => doUpload(e, "banner")} onDelete={delBanner} uploading={uploading === "banner"} testidPrefix="banner" hint="Shown in the rotating hero carousel on the home page (max 8). Landscape images work best." />
      <Gallery title="Certificates" icon={<Award className="text-brand" size={20} />} items={certs} onUpload={(e) => doUpload(e, "cert")} onDelete={delCert} uploading={uploading === "cert"} testidPrefix="cert" hint="Shown in the About Us page gallery." />
    </div>
  );
}
